import { useState, useCallback, useEffect } from "react";
import { FileText, Download, CheckCircle2, Pencil, Check, X, Trash2, Eye, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import ProcessedFileSplitView from "@/components/ProcessedFileSplitView";
import { sanitizeFileName } from "@/lib/pdfDownload";
import type { QuickSupplierCreated } from "@/components/QuickSupplierDialog";

const safeText = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

export interface VatLine {
  taxableBase: number;
  vatRate: number;
  vatAmount: number;
  expenseAccount?: string;
}

export interface ProcessedFile {
  id: string;
  originalName: string;
  baseName: string;
  newName: string;
  date: string;
  supplier: string;
  invoiceNumber: string;
  paymentMethod?: string;
  supplierTaxId?: string;
  originalExtractedTaxId?: string;
  originalExtractedSupplier?: string;
  vatBreakdown?: VatLine[];
  totalAmount?: number;
  irpfRate?: number;
  irpfAmount?: number;
  supplierAccountCode?: string;
  expenseAccountCode?: string;
  supplierDescription?: string;
  isDuplicate?: boolean;
  /** Coincidencia con el histórico permanente (lib/invoiceHistory). */
  historyMatch?: { level: 1 | 2 | 3 | 4; reason: string } | null;
  /** La IA leyó como proveedor un NIF que coincide con la empresa receptora activa. Aviso no bloqueante. */
  receiverConfusion?: boolean;
  /** Factura de abono / rectificativa: los importes van en negativo. */
  isCreditNote?: boolean;
  /** Indicios de abono encontrados en el texto del documento. */
  creditNoteHints?: string[];
  /** "strong" = indicio inequívoco, "weak" = dudoso, "none" = ninguno. */
  creditNoteHintLevel?: "strong" | "weak" | "none";
  /** El usuario cambió el signo a mano: no volver a avisar de abono. */
  creditNoteReviewed?: boolean;
  /** Ya se consultó a la IA una contrapartida para esta factura: no repetir en cada refresco. */
  aiSuggestionAttempted?: boolean;
  /** Serie propia del libro de IVA (si no, serie principal de la sesión). */
  seriesPrefix?: string;
  /** Nº inicial de esa serie. */
  seriesStart?: number;
  totalInconsistency?: boolean;
  calculatedTotal?: number;
  licensePlate?: string;
  /** Zonas del documento de donde la IA leyó cada dato de cabecera. */
  fieldLocations?: import("@/lib/fieldLocations").FieldLocations;
  postalCode?: string;
  contractNumber?: string;
  installmentNumber?: number;
  leasingAmortization?: number;
  leasingInterest?: number;
  leasingCostRecovery?: number;
  leasingFinancialCharge?: number;
  leasingInsurance?: number;
  leasingCostRecoveryAccount?: string;
  leasingFinancialChargeAccount?: string;
  leasingInsuranceAccount?: string;
  /** Si true, el usuario ha editado el desglose IVA manualmente: el auto-apply de leasing/maestro NO debe sobrescribirlo. */
  manualBreakdown?: boolean;
  /** Si true, es un documento fallido promovido al listado que aún NO ha sido validado por el usuario (pendiente de guardar). */
  pendingValidation?: boolean;
  /** Si true, en el SUENLACE se emite un 2º asiento (proveedor → caja/tarjeta) por el total. */
  generatePaymentEntry?: boolean;
  /** Cuenta de contrapartida del asiento de pago (570 caja, 5525 tarjeta…). */
  paymentEntryAccount?: string;
  /** "cash" | "card" — tipo inferido al proponer la cuenta, sirve para mostrar el badge en UI. */
  paymentEntryKind?: "cash" | "card";
  /**
   * Tipo de operación a efectos del SUENLACE/IVA. Si se omite → "interior".
   *  - aib/ais → adquisición intracomunitaria (alimenta modelo 349, doble asiento 477/472).
   *  - isp     → inversión sujeto pasivo (doble asiento 477/472, sin 349).
   *  - importacion → DUA (sin auto-repercusión: la cuota la liquida la aduana).
   */
  operationType?: "interior" | "aib" | "ais" | "isp" | "importacion";
  /** Manual per-invoice choice takes precedence over supplier-master defaults. */
  manualOperationType?: boolean;
  /**
   * Tipo de retención del proveedor. Determina el código de impreso en el SUENLACE
   * y con él el modelo (111 profesionales, 180 arrendamientos, 115 capital, agraria).
   */
  retentionType?: "none" | "mod111" | "mod115" | "mod193";
  /**
   * Deducibilidad del IVA soportado (solo operaciones interiores):
   *  - "full" (por defecto) → subtipo 01 en el SUENLACE
   *  - "none" → subtipo 02 (IVA no deducible)
   *  - "half" → 50 % subtipo 01 + 50 % subtipo 02
   */
  vatDeductibility?: "full" | "none" | "half";
  /** Observaciones libres del usuario. Se concatenan al concepto del asiento en el SUENLACE. */
  notes?: string;
  /** Anexos vinculados (no modifican la factura). */
  attachments?: File[];
  file: File;
}

interface InvoiceListProps {
  files: ProcessedFile[];
  onUpdate?: (id: string, fields: { date: string; supplier: string; invoiceNumber: string; supplierTaxId?: string }) => void;
  onDelete?: (id: string) => void;
  onUpdateNumericField?: (fileId: string, field: "taxableBase" | "vatRate" | "vatAmount" | "irpfRate" | "irpfAmount" | "totalAmount", value: number | string, vatLineIndex?: number) => void;
  onAddVatLine?: (fileId: string) => void;
  onRemoveVatLine?: (fileId: string, vatLineIndex: number) => void;
  /** Optional controlled open id (e.g. from global search). When set, opens the split view. */
  openFileId?: string | null;
  /** Called when the split view is closed (in both controlled and uncontrolled modes). */
  onSplitViewClose?: () => void;
  /** IDs of files currently selected for bulk actions. */
  selectedIds?: Set<string>;
  /** Toggle selection for one file. */
  onToggleSelect?: (id: string) => void;
  /** Select / deselect all visible files. */
  onToggleSelectAll?: () => void;
  /** Empresa aplicada (para cargar catálogo de cuentas en el split view). */
  appliedCompanyId?: string | null;
  /** Editar la cuenta de contrapartida desde el split view. */
  onUpdateExpenseAccount?: (fileId: string, account: string, vatLineIndex?: number) => void;
  onUpdateSupplierAccount?: (fileId: string, account: string) => void;
  onUpdateVatBookField?: (
    fileId: string,
    field: "date" | "supplier" | "supplierTaxId" | "invoiceNumber" | "paymentMethod",
    value: string,
  ) => void;
  onUpdateDescription?: (fileId: string, value: string) => void;
  onQuickSupplierCreated?: (fileId: string, created: QuickSupplierCreated) => void;
  /** Cuentas de pago configuradas para la empresa activa. */
  paymentAccounts?: {
    cashAccount: string;
    cardAccount: string;
    cardAccounts?: { label: string; account: string }[];
  } | null;
  /** Modificar el asiento de pago desde el split view. */
  onUpdatePaymentEntry?: (
    fileId: string,
    fields: { generatePaymentEntry?: boolean; paymentEntryAccount?: string; paymentEntryKind?: "cash" | "card" },
  ) => void;
  /** Cambia el tipo de operación IVA (interior/intracom/ISP/importación) desde el split view. */
  onUpdateOperationType?: (
    fileId: string,
    operationType: "interior" | "aib" | "ais" | "isp" | "importacion",
  ) => void;
  /** Cambia el tipo de retención del documento (clasificación 111/115/180/agraria). */
  onUpdateRetentionType?: (
    fileId: string,
    retentionType: "none" | "mod111" | "mod115" | "mod193",
  ) => void;
  /** Cambia la deducibilidad del IVA de la factura completa. */
  onUpdateVatDeductibility?: (fileId: string, value: "full" | "none" | "half") => void;
  onToggleCreditNote?: (fileId: string, value: boolean) => void;
  /** Abre el separador de PDF para el documento indicado. */
  onSplit?: (file: ProcessedFile) => void;
  onMergeWith?: (file: ProcessedFile, extra: File[]) => void;
  onUpdateNotes?: (fileId: string, value: string) => void;
  onUpdateAttachments?: (fileId: string, files: File[]) => void;
  seriesOptions?: { prefix: string; isMain: boolean }[];
  onChangeSeries?: (fileId: string, prefix: string) => void;
}

const InvoiceList = ({
  files,
  onUpdate,
  onDelete,
  onUpdateNumericField,
  onAddVatLine,
  onRemoveVatLine,
  openFileId,
  onSplitViewClose,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  appliedCompanyId,
  onUpdateExpenseAccount,
  onUpdateSupplierAccount,
  onUpdateVatBookField,
  onUpdateDescription,
  onQuickSupplierCreated,
  paymentAccounts,
  onUpdatePaymentEntry,
  onUpdateOperationType,
  onUpdateRetentionType,
  onUpdateVatDeductibility,
  onToggleCreditNote,
  onSplit,
  onMergeWith,
  onUpdateNotes,
  onUpdateAttachments,
  seriesOptions,
  onChangeSeries,
}: InvoiceListProps) => {
  const [internalSplitId, setInternalSplitId] = useState<string | null>(null);
  const splitViewFileId = openFileId !== undefined && openFileId !== null ? openFileId : internalSplitId;
  const [reviewedIds, setReviewedIds] = useState<Set<string>>(new Set());
  const markReviewed = useCallback((id: string) => {
    setReviewedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);
  const openSplit = useCallback((id: string) => {
    markReviewed(id);
    setInternalSplitId(id);
  }, [markReviewed]);
  useEffect(() => {
    if (openFileId) markReviewed(openFileId);
  }, [openFileId, markReviewed]);

  const closeSplitView = useCallback(() => {
    setInternalSplitId(null);
    onSplitViewClose?.();
  }, [onSplitViewClose]);

  const handleDownload = useCallback((pf: ProcessedFile) => {
    // Single-file download: descarga directa (sin ZIP), estampando el post-it
    // de Observaciones igual que en la descarga múltiple.
    window.setTimeout(async () => {
      try {
        const { stampNoteOnDocument } = await import("@/lib/stampNote");
        const stamped = await stampNoteOnDocument(pf.file, pf.notes);
        const url = URL.createObjectURL(stamped);
        const a = document.createElement("a");
        a.href = url;
        a.download = sanitizeFileName(pf.newName);
        a.rel = "noopener";
        a.click();
        window.setTimeout(() => {
          try { URL.revokeObjectURL(url); } catch { /* ignore */ }
        }, 120000);
      } catch (e) {
        console.error("Download error:", e);
      }
    }, 0);
  }, []);


  const handleSplitViewSave = useCallback((id: string, fields: { date: string; supplier: string; invoiceNumber: string; supplierTaxId?: string }) => {
    onUpdate?.(id, fields);
  }, [onUpdate]);

  const splitViewFile = splitViewFileId ? files.find((f) => f.id === splitViewFileId) ?? null : null;
  const splitViewIndex = splitViewFileId ? files.findIndex((f) => f.id === splitViewFileId) : -1;
  const isControlledOpen = openFileId !== undefined && openFileId !== null;
  const canNavigate = !isControlledOpen && splitViewIndex >= 0 && files.length > 1;
  const handleNavigate = useCallback((dir: -1 | 1) => {
    if (splitViewIndex < 0) return;
    const next = splitViewIndex + dir;
    if (next < 0 || next >= files.length) return;
    const nextId = files[next].id;
    markReviewed(nextId);
    setInternalSplitId(nextId);
  }, [splitViewIndex, files, markReviewed]);

  if (files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <FileText className="mb-3 h-12 w-12 text-muted-foreground/40" />
        <p className="text-sm font-medium text-muted-foreground">
          Aún no has procesado facturas
        </p>
        <p className="mt-1 text-xs text-muted-foreground/70">
          Sube un PDF y completa los datos para empezar
        </p>
      </div>
    );
  }

  const selectionEnabled = !!onToggleSelect;
  const allSelected = selectionEnabled && files.length > 0 && files.every((f) => selectedIds?.has(f.id));
  const someSelected = selectionEnabled && !!selectedIds && selectedIds.size > 0 && !allSelected;

  return (
    <div className="space-y-3">
      {selectionEnabled && (
        <div className="flex items-center gap-2 px-1">
          <Checkbox
            checked={allSelected ? true : someSelected ? "indeterminate" : false}
            onCheckedChange={() => onToggleSelectAll?.()}
            aria-label="Seleccionar todas"
          />
          <span className="text-xs text-muted-foreground">
            {selectedIds && selectedIds.size > 0
              ? `${selectedIds.size} seleccionada${selectedIds.size === 1 ? "" : "s"}`
              : "Seleccionar todas"}
          </span>
        </div>
      )}
      {files.filter(Boolean).map((pf) => {
        const isSelected = selectedIds?.has(pf.id) ?? false;
        const isReviewed = reviewedIds.has(pf.id);
        const displayName = safeText(pf.newName) || safeText(pf.originalName) || "Factura procesada";
        const originalName = safeText(pf.originalName) || "Archivo original";
        return (
        <div
          key={pf.id}
          className={`group rounded-lg border p-4 transition-all hover:shadow-md cursor-pointer ${isSelected ? "ring-2 ring-primary/40" : ""} ${isReviewed ? "bg-muted/60 border-primary/30 opacity-80" : "bg-card"}`}
          onClick={() => openSplit(pf.id)}
        >
          <div className="flex items-center gap-3">
            {selectionEnabled && (
              <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => onToggleSelect?.(pf.id)}
                  aria-label={`Seleccionar ${pf.newName}`}
                />
              </div>
            )}
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${pf.pendingValidation ? "bg-yellow-500/20" : "bg-accent"}`}>
              {pf.pendingValidation ? (
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-primary" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="truncate text-sm font-semibold text-foreground">
                  {displayName}
                </p>
                {pf.pendingValidation && (
                  <Badge className="shrink-0 gap-1 bg-yellow-500 text-white hover:bg-yellow-600 text-[10px] px-1.5 py-0">
                    <AlertTriangle className="h-3 w-3" />
                    Pendiente de validar
                  </Badge>
                )}
                {pf.isCreditNote && (
                  <Badge
                    className="shrink-0 gap-1 bg-red-600 text-white hover:bg-red-700 text-[10px] px-1.5 py-0"
                    title={
                      pf.creditNoteHints?.length
                        ? `Abono / rectificativa: ${pf.creditNoteHints.join(", ")}`
                        : "Factura de abono / rectificativa: los importes van en negativo."
                    }
                  >
                    ABONO
                  </Badge>
                )}
                {pf.isDuplicate && (
                  <Badge className="shrink-0 gap-1 bg-orange-500 text-white hover:bg-orange-600 text-[10px] px-1.5 py-0">
                    <AlertTriangle className="h-3 w-3" />
                    Posible duplicado
                  </Badge>
                )}
                {pf.historyMatch && (
                  <Badge
                    className={`shrink-0 gap-1 text-white text-[10px] px-1.5 py-0 ${
                      pf.historyMatch.level === 4 ? "bg-orange-500 hover:bg-orange-600" : "bg-red-600 hover:bg-red-700"
                    }`}
                    title={pf.historyMatch.reason}
                  >
                    <AlertTriangle className="h-3 w-3" />
                    {pf.historyMatch.level === 1
                      ? "Duplicado (NIF+Nº+Base)"
                      : pf.historyMatch.level === 2
                      ? "Duplicado (NIF+Nº factura)"
                      : pf.historyMatch.level === 3
                      ? "Duplicado (NIF+fecha+total)"
                      : "Posible duplicado histórico"}
                  </Badge>
                )}
                {pf.receiverConfusion && (
                  <Badge
                    className="shrink-0 gap-1 bg-yellow-500 text-white hover:bg-yellow-600 text-[10px] px-1.5 py-0"
                    title="El NIF leído como proveedor coincide con la empresa receptora activa. Verifica que la factura no esté invertida y corrígela si es necesario."
                  >
                    <AlertTriangle className="h-3 w-3" />
                    Revisar emisor/receptor
                  </Badge>
                )}
                {pf.vatDeductibility && pf.vatDeductibility !== "full" && (
                  <Badge
                    variant="outline"
                    className="shrink-0 text-[10px] px-1.5 py-0"
                    title="Deducibilidad del IVA marcada manualmente. Se traslada al SUENLACE con subtipo 02."
                  >
                    {pf.vatDeductibility === "none" ? "IVA no deducible" : "IVA 50 %"}
                  </Badge>
                )}
              </div>

              <p className="truncate text-xs text-muted-foreground">
                {pf.supplierTaxId && <span className="font-medium">{pf.supplierTaxId} · </span>}
                Original: {originalName}
              </p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); openSplit(pf.id); }}
              className="rounded-md p-2 text-muted-foreground transition-all hover:bg-accent hover:text-primary"
              title="Ver y editar"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleDownload(pf); }}
              className="rounded-md p-2 text-muted-foreground transition-all hover:bg-accent hover:text-primary"
              title="Descargar"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete?.(pf.id); }}
              className="rounded-md p-2 text-muted-foreground transition-all hover:bg-destructive/10 hover:text-destructive"
              title="Desechar"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        );
      })}
      <ProcessedFileSplitView
        file={splitViewFile}
        open={splitViewFileId !== null}
        onClose={closeSplitView}
        onSave={handleSplitViewSave}
        onDownload={handleDownload}
        onUpdateNumericField={onUpdateNumericField}
        onAddVatLine={onAddVatLine}
        onRemoveVatLine={onRemoveVatLine}
        appliedCompanyId={appliedCompanyId}
        onUpdateExpenseAccount={onUpdateExpenseAccount}
        onUpdateSupplierAccount={onUpdateSupplierAccount}
        onUpdateVatBookField={onUpdateVatBookField}
        onUpdateDescription={onUpdateDescription}
        onQuickSupplierCreated={onQuickSupplierCreated}
        paymentAccounts={paymentAccounts}
        onUpdatePaymentEntry={onUpdatePaymentEntry}
        onUpdateOperationType={onUpdateOperationType}
        onUpdateRetentionType={onUpdateRetentionType}
        onUpdateVatDeductibility={onUpdateVatDeductibility}
        onToggleCreditNote={onToggleCreditNote}
        onSplit={onSplit}
        onMergeWith={onMergeWith}
        onUpdateNotes={onUpdateNotes}
        onUpdateAttachments={onUpdateAttachments}
        seriesOptions={seriesOptions}
        onChangeSeries={onChangeSeries}
        onNavigate={canNavigate ? handleNavigate : undefined}
        hasPrev={canNavigate && splitViewIndex > 0}
        hasNext={canNavigate && splitViewIndex >= 0 && splitViewIndex < files.length - 1}
        positionLabel={splitViewIndex >= 0 ? `${splitViewIndex + 1} / ${files.length}` : undefined}
        onDiscard={onDelete}
      />
    </div>
  );
};

export default InvoiceList;
